import { supabase } from '../lib/supabase.js';

/**
 * Timeline Interpolation Service
 * 
 * Replaces fixed timeline pressure categories with flexible min/max bounds interpolation.
 * Calculates student timeline position and interpolates milestone due dates within configured bounds.
 */
class TimelineInterpolationService {

  /**
   * Calculate where a student is in their college application timeline
   * Returns 0.0 (started early/May 1) to 1.0 (started late/Sep 10)
   * Based on when they started relative to ideal timeline boundaries
   */
  static async calculateStudentPosition(student) {
    try {
      console.log(`\n🚨 [TIMELINE POSITION DEBUG] 🚨`);
      console.log(`[TimelineInterpolationService] ===== STUDENT POSITION CALCULATION =====`);
      console.log(`[TimelineInterpolationService] Student ID: ${student.id}`);
      console.log(`[TimelineInterpolationService] Student name: ${student.first_name || 'Unknown'} ${student.last_name || 'Student'}`);

      // CRITICAL: Debug date source selection
      console.log(`[TimelineInterpolationService] 🔍 DATE SOURCE ANALYSIS:`);
      console.log(`[TimelineInterpolationService]   timeline_baseline_date: ${student.timeline_baseline_date || 'null'}`);
      console.log(`[TimelineInterpolationService]   created_at: ${student.created_at || 'null'}`);
      console.log(`[TimelineInterpolationService]   fallback: ${new Date().toISOString().split('T')[0]}`);

      // Get student's actual planning start date
      const actualStartDate = new Date(student.timeline_baseline_date || student.created_at || new Date());
      console.log(`[TimelineInterpolationService] 🎯 SELECTED START DATE: ${actualStartDate.toDateString()} (${actualStartDate.toISOString().split('T')[0]})`);

      // CRITICAL: Debug graduation year logic
      console.log(`[TimelineInterpolationService] 🎓 GRADUATION YEAR ANALYSIS:`);
      console.log(`[TimelineInterpolationService]   student.graduation_year: ${student.graduation_year || 'null'}`);

      let graduationYear;
      if (student.graduation_year) {
        graduationYear = student.graduation_year;
        console.log(`[TimelineInterpolationService]   ✅ Using explicit graduation year: ${graduationYear}`);
      } else {
        // Infer graduation year from when they started planning
        const startYear = actualStartDate.getFullYear();
        const startMonth = actualStartDate.getMonth(); // 0 = Jan, 8 = Sep

        console.log(`[TimelineInterpolationService]   📅 Inferring from start date:`);
        console.log(`[TimelineInterpolationService]     Start year: ${startYear}`);
        console.log(`[TimelineInterpolationService]     Start month: ${startMonth} (${actualStartDate.toLocaleString('default', { month: 'long' })})`);

        // For 2025-2026 academic cycle:
        // - Anyone starting planning during 2024 or early 2025 (before June) is likely a rising senior → graduates 2026
        // - Anyone starting during summer/fall 2025 (June-Dec) is likely a current senior → graduates 2026
        // - Anyone starting during 2026 is likely behind and still graduating 2026 (crisis mode)

        if (startYear === 2025 || startYear === 2024) {
          // 2025-2026 academic cycle - all students graduate in 2026
          graduationYear = 2026;
          console.log(`[TimelineInterpolationService]     ✅ 2024/2025 start → Class of 2026`);
        } else if (startYear >= 2026) {
          // Future years - they graduate the following spring
          graduationYear = startYear + 1;
          console.log(`[TimelineInterpolationService]     ✅ ${startYear} start → Class of ${graduationYear}`);
        } else {
          // Past years (2023 and earlier) - likely already graduated or late
          graduationYear = startYear + 2; // Conservative assumption
          console.log(`[TimelineInterpolationService]     ⚠️  Past year ${startYear} → Conservative Class of ${graduationYear}`);
        }
      }
      
      const juniorYear = graduationYear - 2;
      const seniorYear = graduationYear - 1;

      console.log(`[TimelineInterpolationService] 📚 ACADEMIC TIMELINE:`);
      console.log(`[TimelineInterpolationService]   Graduation year: ${graduationYear}`);
      console.log(`[TimelineInterpolationService]   Junior year: ${juniorYear}`);
      console.log(`[TimelineInterpolationService]   Senior year: ${seniorYear}`);

      // Define timeline boundaries
      const maxTimelineStart = new Date(juniorYear, 4, 10); // May 10 of junior year (early planners)
      const minTimelineStart = new Date(seniorYear, 8, 10); // Sep 10 of senior year (late starters)

      console.log(`[TimelineInterpolationService] 📏 TIMELINE BOUNDARIES:`);
      console.log(`[TimelineInterpolationService]   Early start (0.0): ${maxTimelineStart.toDateString()} (${maxTimelineStart.toISOString().split('T')[0]})`);
      console.log(`[TimelineInterpolationService]   Crisis start (1.0): ${minTimelineStart.toDateString()} (${minTimelineStart.toISOString().split('T')[0]})`);

      // CRITICAL: Debug position calculation logic
      console.log(`[TimelineInterpolationService] ⚖️  POSITION CALCULATION:`);
      console.log(`[TimelineInterpolationService]   Student start: ${actualStartDate.toISOString().split('T')[0]}`);
      console.log(`[TimelineInterpolationService]   Early boundary: ${maxTimelineStart.toISOString().split('T')[0]}`);
      console.log(`[TimelineInterpolationService]   Crisis boundary: ${minTimelineStart.toISOString().split('T')[0]}`);

      // Calculate position based on when they started
      let position;

      if (actualStartDate <= maxTimelineStart) {
        // Started early or on time for max timeline
        position = 0.0;
        console.log(`[TimelineInterpolationService]   🟢 EARLY: ${actualStartDate.toDateString()} <= ${maxTimelineStart.toDateString()}`);
        console.log(`[TimelineInterpolationService]   ✅ Position: 0.0 (max timeline/early starter)`);
      } else if (actualStartDate >= minTimelineStart) {
        // Started at or after crisis point
        position = 1.0;
        console.log(`[TimelineInterpolationService]   🔴 CRISIS: ${actualStartDate.toDateString()} >= ${minTimelineStart.toDateString()}`);
        console.log(`[TimelineInterpolationService]   ✅ Position: 1.0 (min timeline/crisis mode)`);
      } else {
        // Started between boundaries - interpolate precisely
        const totalSpan = minTimelineStart.getTime() - maxTimelineStart.getTime();
        const studentOffset = actualStartDate.getTime() - maxTimelineStart.getTime();
        position = studentOffset / totalSpan;

        const daysFromMax = Math.floor(studentOffset / (1000 * 60 * 60 * 24));
        const totalDays = Math.floor(totalSpan / (1000 * 60 * 60 * 24));
        console.log(`[TimelineInterpolationService]   🟡 INTERPOLATED: Between boundaries`);
        console.log(`[TimelineInterpolationService]   Days from early: ${daysFromMax} / ${totalDays}`);
        console.log(`[TimelineInterpolationService]   ✅ Position: ${position.toFixed(3)} (${Math.round(position * 100)}%)`);
      }

      console.log(`[TimelineInterpolationService] 🎯 FINAL RESULT: Position = ${position}`);
      console.log(`[TimelineInterpolationService] ===== END STUDENT POSITION CALCULATION =====\n`);

      return position;
      
    } catch (error) {
      console.error('[TimelineInterpolationService] Error calculating student position:', error);
      return 0.5; // Safe fallback to middle position
    }
  }

  /**
   * Get the earliest application deadline for a student across all their colleges
   */
  static async getEarliestApplicationDeadline(studentId) {
    try {
      const { data: colleges, error } = await supabase
        .from('college_journey_colleges')
        .select(`
          application_round,
          global_colleges!global_college_id(
            ed_deadline, ed2_deadline, ea_deadline, rd_deadline
          )
        `)
        .eq('user_id', studentId);

      if (error) {
        console.error('[TimelineInterpolationService] Error fetching college deadlines:', error);
        return null;
      }

      let earliestDeadline = null;
      const currentDate = new Date();
      
      for (const college of colleges || []) {
        const deadlines = [
          college.global_colleges?.ed_deadline,
          college.global_colleges?.ed2_deadline, 
          college.global_colleges?.ea_deadline,
          college.global_colleges?.rd_deadline
        ].filter(Boolean).map(d => new Date(d));
        
        for (const deadline of deadlines) {
          if (deadline > currentDate && (!earliestDeadline || deadline < earliestDeadline)) {
            earliestDeadline = deadline;
          }
        }
      }

      // If no future deadlines found, use next academic year's typical deadline
      if (!earliestDeadline) {
        const academicYear = new Date().getFullYear() + 1;
        earliestDeadline = new Date(`${academicYear}-01-01`); // Default RD deadline
        console.log(`[TimelineInterpolationService] No college deadlines found, using default: ${earliestDeadline.toDateString()}`);
      }

      return earliestDeadline.toISOString().split('T')[0];
      
    } catch (error) {
      console.error('[TimelineInterpolationService] Error in getEarliestApplicationDeadline:', error);
      return null;
    }
  }

  /**
   * Interpolate milestone offset based on student position and template bounds
   * Uses signed offsets directly from the database
   *
   * @param {Object} template - Milestone template with signed min/max offset bounds
   * @param {number} studentPosition - Student position (0.0 to 1.0)
   * @param {string} context - Application context ('rd' or 'ed')
   * @returns {number} - Signed offset in days (negative = before anchor, positive = after)
   */
  static interpolateOffset(template, studentPosition, context) {
    try {
      console.log(`\n🚨 [OFFSET INTERPOLATION DEBUG] 🚨`);
      console.log(`[TimelineInterpolationService] ===== OFFSET INTERPOLATION =====`);
      console.log(`[TimelineInterpolationService] Template: ${template.title} (ID: ${template.id})`);
      console.log(`[TimelineInterpolationService] Context: ${context}`);
      console.log(`[TimelineInterpolationService] Student Position: ${studentPosition.toFixed(3)} (${Math.round(studentPosition * 100)}%)`);

      // Determine which bounds to use based on context
      // Offsets are already signed in the database (negative = before, positive = after)
      let maxOffset, minOffset;

      console.log(`[TimelineInterpolationService] 🎯 BOUNDS SELECTION:`);
      console.log(`[TimelineInterpolationService]   Template bounds available:`, {
        max_ed_offset_days: template.max_ed_offset_days,
        min_ed_offset_days: template.min_ed_offset_days,
        max_rd_offset_days: template.max_rd_offset_days,
        min_rd_offset_days: template.min_rd_offset_days
      });

      if (context === 'ed' || context === 'ea') {
        // Use ED bounds if available, fallback to RD
        maxOffset = template.max_ed_offset_days !== null ? template.max_ed_offset_days : template.max_rd_offset_days;
        minOffset = template.min_ed_offset_days !== null ? template.min_ed_offset_days : template.min_rd_offset_days;
        console.log(`[TimelineInterpolationService]   ✅ EARLY context → Using ED bounds (with RD fallback)`);
        console.log(`[TimelineInterpolationService]     max_ed_offset_days: ${template.max_ed_offset_days} → selected: ${maxOffset}`);
        console.log(`[TimelineInterpolationService]     min_ed_offset_days: ${template.min_ed_offset_days} → selected: ${minOffset}`);
      } else {
        // Use RD bounds
        maxOffset = template.max_rd_offset_days;
        minOffset = template.min_rd_offset_days;
        console.log(`[TimelineInterpolationService]   ✅ REGULAR context → Using RD bounds`);
        console.log(`[TimelineInterpolationService]     max_rd_offset_days: ${maxOffset}`);
        console.log(`[TimelineInterpolationService]     min_rd_offset_days: ${minOffset}`);
      }

      // Validate bounds exist
      if (maxOffset === null || maxOffset === undefined ||
          minOffset === null || minOffset === undefined) {
        console.log(`[TimelineInterpolationService] ❌ No bounds defined, falling back to legacy offset: ${template.offset_days}`);
        return template.offset_days || 7;
      }

      console.log(`[TimelineInterpolationService] ⚖️  INTERPOLATION MATH:`);
      console.log(`[TimelineInterpolationService]   Selected bounds (signed): max=${maxOffset}, min=${minOffset}`);
      console.log(`[TimelineInterpolationService]   Position: ${studentPosition.toFixed(3)}`);
      console.log(`[TimelineInterpolationService]   Inverse position: ${(1 - studentPosition).toFixed(3)}`);

      // CORRECT FORMULA for signed offsets:
      // Position 0.0 (early start) = max offset (most time)
      // Position 1.0 (late start) = min offset (least time)
      // This works for both negative offsets (before anchor) and positive (after anchor)
      const interpolatedOffset = Math.round(
        minOffset + (maxOffset - minOffset) * (1 - studentPosition)
      );

      console.log(`[TimelineInterpolationService]   📐 Formula: ${minOffset} + (${maxOffset} - ${minOffset}) * ${(1 - studentPosition).toFixed(3)}`);
      console.log(`[TimelineInterpolationService]   📐 Calculation: ${minOffset} + ${(maxOffset - minOffset)} * ${(1 - studentPosition).toFixed(3)}`);
      console.log(`[TimelineInterpolationService]   📐 Step 1: ${minOffset} + ${((maxOffset - minOffset) * (1 - studentPosition)).toFixed(3)}`);
      console.log(`[TimelineInterpolationService]   📐 Before rounding: ${(minOffset + (maxOffset - minOffset) * (1 - studentPosition)).toFixed(3)}`);
      console.log(`[TimelineInterpolationService]   🎯 FINAL OFFSET: ${interpolatedOffset} days`);
      
      // Critical validation for August 26 bug
      if (studentPosition === 1.0) {
        console.log(`[TimelineInterpolationService] 🚨 CRISIS MODE VALIDATION:`);
        console.log(`[TimelineInterpolationService]   Position 1.0 should use MIN bound: ${minOffset}`);
        console.log(`[TimelineInterpolationService]   Calculated offset: ${interpolatedOffset}`);
        console.log(`[TimelineInterpolationService]   Match expected? ${interpolatedOffset === minOffset ? '✅ YES' : '❌ NO - BUG DETECTED!'}`);
      }
      
      console.log(`[TimelineInterpolationService] ===== END OFFSET INTERPOLATION =====\n`);

      return interpolatedOffset;
      
    } catch (error) {
      console.error('[TimelineInterpolationService] Error in interpolateOffset:', error);
      return this.fallbackToLegacyOffset(template);
    }
  }

  /**
   * Fallback to legacy offset calculation when bounds are not available
   */
  static fallbackToLegacyOffset(template) {
    console.log(`[TimelineInterpolationService] Using legacy offset: ${template.offset_days}d`);
    return template.offset_days || 7;
  }

  /**
   * Get reference anchor date for calculating offsets from target dates
   * Uses the student's actual graduation year with proper inference fallback
   */
  static async getReferenceAnchorDate(anchorId, student) {
    // Use same graduation year logic as calculateStudentPosition
    let graduationYear;
    if (student.graduation_year) {
      graduationYear = student.graduation_year;
    } else {
      const actualStartDate = new Date(student.timeline_baseline_date || student.created_at || new Date());
      const startYear = actualStartDate.getFullYear();

      if (startYear === 2025 || startYear === 2024) {
        graduationYear = 2026; // 2025-2026 academic cycle
      } else if (startYear >= 2026) {
        graduationYear = startYear + 1;
      } else {
        graduationYear = startYear + 2; // Conservative assumption
      }
    }

    console.log(`[TimelineInterpolationService] Using graduation year for reference: ${graduationYear}`);

    // Create a reference student for anchor calculation
    const referenceStudent = {
      graduation_year: graduationYear,
      timeline_baseline_date: new Date(graduationYear - 1, 8, 10) // Sep 10 of senior year for min timeline
    };

    // Special handling for timeline_baseline anchor
    if (anchorId === 'timeline_baseline') {
      // For min timeline (Sep 10), return Sep 10 of the reference year
      return new Date(graduationYear - 1, 8, 10);
    }

    // For other anchors, calculate based on graduation year
    const AnchorRegistryService = (await import('./AnchorRegistryService')).default;
    return await AnchorRegistryService.calculateAnchorDate(anchorId, referenceStudent);
  }

  /**
   * Determine the appropriate context based on college application round
   */
  static determineContext(applicationRound) {
    switch (applicationRound) {
      case 'early_decision':
      case 'early_decision_2':
        return 'ed';
      case 'early_action':
        return 'ea';
      case 'regular_decision':
      default:
        return 'rd';
    }
  }

  /**
   * Calculate due date using interpolation system with target dates
   */
  static async calculateInterpolatedDueDate(template, student, college = null) {
    try {
      console.log(`\n[TimelineInterpolationService] ===== INTERPOLATED CALCULATION =====`);
      console.log(`[TimelineInterpolationService] Template: ${template.title}`);
      console.log(`[TimelineInterpolationService] Student: ${student.id}`);

      // Step 1: Calculate student timeline position
      const studentPosition = await this.calculateStudentPosition(student);

      // Step 2: Determine context from college application round
      const context = this.determineContext(college?.application_round || 'regular_decision');

      // Step 3: Calculate offsets - PREFER offset bounds over target dates
      let interpolatedOffset;

      // Check if we have offset bounds (preferred method)
      const hasOffsetBounds = (context === 'ed' || context === 'ea')
        ? (template.max_ed_offset_days !== null && template.min_ed_offset_days !== null)
        : (template.max_rd_offset_days !== null && template.min_rd_offset_days !== null);

      if (hasOffsetBounds) {
        // Primary method: Use offset bounds for calculation
        console.log(`[TimelineInterpolationService] Using offset bounds for calculation`);
        interpolatedOffset = this.interpolateOffset(template, studentPosition, context);
      } else {
        // Fallback method: Calculate from target dates if no offset bounds exist
        console.log(`[TimelineInterpolationService] No offset bounds, falling back to target dates`);

        let maxTargetDate, minTargetDate;
        if (context === 'ed' || context === 'ea') {
          maxTargetDate = template.max_ed_target_date;
          minTargetDate = template.min_ed_target_date;
        } else {
          maxTargetDate = template.max_rd_target_date;
          minTargetDate = template.min_rd_target_date;
        }

        if (maxTargetDate && minTargetDate && template.anchor_id) {
        console.log(`[TimelineInterpolationService] Calculating offsets from target dates`);
        console.log(`[TimelineInterpolationService]   Max target: ${maxTargetDate}`);
        console.log(`[TimelineInterpolationService]   Min target: ${minTargetDate}`);
        console.log(`[TimelineInterpolationService]   Anchor: ${template.anchor_id}`);
        console.log(`[TimelineInterpolationService]   Position: ${studentPosition.toFixed(3)}`);

        // Get the anchor date for the student's graduation year
        const referenceAnchor = await this.getReferenceAnchorDate(template.anchor_id, student);

        // Calculate offsets from target dates to reference anchor
        const maxDate = new Date(maxTargetDate);
        const minDate = new Date(minTargetDate);
        const maxOffsetDays = Math.round((maxDate - referenceAnchor) / (1000 * 60 * 60 * 24));
        const minOffsetDays = Math.round((minDate - referenceAnchor) / (1000 * 60 * 60 * 24));

        console.log(`[TimelineInterpolationService]   Calculated max offset: ${maxOffsetDays} days`);
        console.log(`[TimelineInterpolationService]   Calculated min offset: ${minOffsetDays} days`);

        // Interpolate between the calculated offsets
          interpolatedOffset = Math.round(minOffsetDays + (maxOffsetDays - minOffsetDays) * (1 - studentPosition));
          console.log(`[TimelineInterpolationService]   Interpolated offset: ${interpolatedOffset} days`);
        } else {
          // No target dates available either, use template's legacy offset
          console.log(`[TimelineInterpolationService] No target dates available, using legacy offset: ${template.offset_days || 0}`);
          interpolatedOffset = template.offset_days || 0;
        }
      }

      // Step 4: Calculate anchor date with fallback support
      const AnchorRegistryService = (await import('./AnchorRegistryService')).default;
      
      // Use new fallback-aware method if template has fallback configured
      let anchorDate;
      if (template.fallback_anchor_id) {
        anchorDate = await AnchorRegistryService.calculateAnchorDateWithFallback(template, student, college);
      } else {
        // Use traditional method for templates without fallback
        anchorDate = await AnchorRegistryService.calculateAnchorDate(
          template.anchor_id || 'timeline_baseline',
          student,
          college,
          template.depends_on_template_id
        );
      }

      // Step 5: Apply interpolated offset (signed offset - just add it)
      const dueDate = new Date(anchorDate);
      dueDate.setDate(dueDate.getDate() + interpolatedOffset);

      console.log(`[TimelineInterpolationService] 🔧 FINAL CALCULATION:`);
      console.log(`[TimelineInterpolationService]   Anchor: ${anchorDate.toDateString()} (${anchorDate.toISOString().split('T')[0]})`);
      console.log(`[TimelineInterpolationService]   Offset: ${interpolatedOffset} days`);
      console.log(`[TimelineInterpolationService]   Result: ${dueDate.toDateString()} (${dueDate.toISOString().split('T')[0]})`);

      // 🚨 PRODUCTION VALIDATION: Critical bug detection
      const studentStartDate = new Date(student.timeline_baseline_date || student.created_at);
      const isBeforeStart = dueDate < studentStartDate;
      const daysDifference = Math.round((dueDate - studentStartDate) / (1000 * 60 * 60 * 24));

      console.log(`[TimelineInterpolationService] 🚨 PRODUCTION VALIDATION:`);
      console.log(`[TimelineInterpolationService]   Student planning start: ${studentStartDate.toDateString()} (${studentStartDate.toISOString().split('T')[0]})`);
      console.log(`[TimelineInterpolationService]   Task due date: ${dueDate.toDateString()} (${dueDate.toISOString().split('T')[0]})`);
      console.log(`[TimelineInterpolationService]   Days difference: ${daysDifference} (${daysDifference >= 0 ? 'after start' : 'BEFORE START!'})`);
      console.log(`[TimelineInterpolationService]   Validation: ${isBeforeStart ? '❌ CRITICAL BUG - BEFORE START DATE!' : '✅ Valid - after start date'}`);

      if (isBeforeStart) {
        console.error(`[TimelineInterpolationService] 🔥🔥🔥 AUGUST 26 BUG DETECTED! 🔥🔥🔥`);
        console.error(`[TimelineInterpolationService] Task "${template.title}" scheduled for ${dueDate.toISOString().split('T')[0]}`);
        console.error(`[TimelineInterpolationService] But student "${student.first_name} ${student.last_name}" didn't start until ${studentStartDate.toISOString().split('T')[0]}`);
        console.error(`[TimelineInterpolationService] This is ${Math.abs(daysDifference)} days BEFORE they started planning!`);
        console.error(`[TimelineInterpolationService] Check: Position=${studentPosition}, Offset=${interpolatedOffset}, Anchor=${anchorDate.toISOString().split('T')[0]}`);
      }

      console.log(`[TimelineInterpolationService] ===== END INTERPOLATED CALCULATION =====\n`);

      return dueDate;
      
    } catch (error) {
      console.error('[TimelineInterpolationService] 🔥 ERROR in calculateInterpolatedDueDate:', error);
      console.error('[TimelineInterpolationService] Template that failed:', {
        title: template.title,
        anchor_id: template.anchor_id,
        offset_days: template.offset_days
      });

      // Better fallback: use template's offset from anchor instead of today + X
      try {
        const AnchorRegistryService = (await import('./AnchorRegistryService')).default;
        const anchorDate = await AnchorRegistryService.calculateAnchorDate(
          template.anchor_id || 'timeline_baseline',
          student,
          college
        );

        const fallbackDate = new Date(anchorDate);
        // Use signed offset directly - should already be negative for "before" tasks
        const offsetDays = template.offset_days || 7;
        fallbackDate.setDate(fallbackDate.getDate() + offsetDays);

        console.log(`[TimelineInterpolationService] Using offset-based fallback: ${anchorDate.toISOString().split('T')[0]} + ${offsetDays} days = ${fallbackDate.toISOString().split('T')[0]}`);
        return fallbackDate;

      } catch (fallbackError) {
        console.error('[TimelineInterpolationService] Even fallback failed:', fallbackError);
        // Last resort: today + minimum_lead_days
        const lastResort = new Date();
        lastResort.setDate(lastResort.getDate() + (template.minimum_lead_days || 7));
        return lastResort;
      }
    }
  }

  /**
   * Reset student timeline to new baseline date
   * Useful when students fall behind and need a fresh start
   */
  static async resetStudentTimeline(studentId, newBaselineDate = null) {
    try {
      console.log(`[TimelineInterpolationService] Resetting timeline for student ${studentId}`);
      
      const baseline = newBaselineDate || new Date().toISOString().split('T')[0];
      
      // Update student baseline date and increment reset counter
      const { error: updateError } = await supabase
        .from('student_academic_profiles')
        .update({
          timeline_baseline_date: baseline,
          timeline_reset_count: supabase.raw('COALESCE(timeline_reset_count, 0) + 1'),
          last_timeline_calculation: new Date().toISOString()
        })
        .eq('user_id', studentId);
      
      if (updateError) {
        throw updateError;
      }
      
      console.log(`[TimelineInterpolationService] Timeline reset successful. New baseline: ${baseline}`);
      
      return {
        success: true,
        newBaseline: baseline,
        message: 'Timeline reset successfully. Regenerate milestones to apply new dates.'
      };
      
    } catch (error) {
      console.error('[TimelineInterpolationService] Error resetting timeline:', error);
      throw new Error(`Timeline reset failed: ${error.message}`);
    }
  }

  /**
   * Suggest schedule adjustments for workload balancing
   * Finds tasks that can be moved within their bounds to balance workload
   */
  static async suggestScheduleAdjustments(studentId, overloadedDateRanges = []) {
    try {
      console.log(`[TimelineInterpolationService] Analyzing schedule for student ${studentId}`);
      
      // Get student's current milestones
      const { data: milestones, error } = await supabase
        .from('timeline_items')
        .select(`
          id, title, due_date, estimated_duration, 
          metadata, source_type, source_id
        `)
        .eq('user_id', studentId)
        .eq('source_type', 'milestone_template')
        .order('due_date');
      
      if (error) {
        throw error;
      }
      
      const suggestions = [];
      
      for (const milestone of milestones || []) {
        const templateId = milestone.metadata?.template_id;
        if (!templateId) continue;
        
        // Get template to check bounds
        const { data: template, error: templateError } = await supabase
          .from('milestone_templates')
          .select('*')
          .eq('id', templateId)
          .single();
        
        if (templateError || !template) continue;
        
        // Check if milestone has flexible bounds
        const hasFlexibleBounds = (
          template.max_rd_offset_days && template.min_rd_offset_days &&
          Math.abs(template.max_rd_offset_days - template.min_rd_offset_days) > 7
        );
        
        if (hasFlexibleBounds) {
          // This milestone could potentially be rescheduled
          suggestions.push({
            milestoneId: milestone.id,
            title: milestone.title,
            currentDate: milestone.due_date,
            flexibility: Math.abs(template.max_rd_offset_days - template.min_rd_offset_days),
            canMoveEarlier: true, // Could calculate based on current position vs bounds
            canMoveLater: true,
            reason: 'Milestone has flexible scheduling bounds'
          });
        }
      }
      
      console.log(`[TimelineInterpolationService] Found ${suggestions.length} flexible milestones`);
      return suggestions;
      
    } catch (error) {
      console.error('[TimelineInterpolationService] Error in suggestScheduleAdjustments:', error);
      return [];
    }
  }

  /**
   * Validate that template bounds are logical and feasible
   */
  static validateTemplateBounds(template) {
    const contexts = ['max_rd', 'min_rd', 'max_ed', 'min_ed'];
    const issues = [];
    
    for (const context of contexts) {
      const maxField = context.includes('max_') ? `${context}_offset_days` : `max_${context.substring(4)}_offset_days`;
      const minField = context.includes('min_') ? `${context}_offset_days` : `min_${context.substring(4)}_offset_days`;
      
      const maxOffset = template[maxField];
      const minOffset = template[minField];
      
      if (maxOffset !== null && minOffset !== null) {
        if (minOffset > maxOffset) {
          issues.push(`${context}: min offset (${minOffset}) greater than max offset (${maxOffset})`);
        }
        
        if (minOffset < 1) {
          issues.push(`${context}: min offset (${minOffset}) less than 1 day`);
        }
        
        if (maxOffset > 365) {
          issues.push(`${context}: max offset (${maxOffset}) greater than 1 year`);
        }
      }
    }
    
    return {
      isValid: issues.length === 0,
      issues
    };
  }

  /**
   * Get timeline statistics for analytics and monitoring
   */
  static async getTimelineStatistics(studentId) {
    try {
      const { data: student } = await supabase
        .from('student_academic_profiles')
        .select('timeline_baseline_date, timeline_reset_count, last_timeline_calculation')
        .eq('user_id', studentId)
        .single();
      
      const position = await this.calculateStudentPosition({ id: studentId, ...student });
      const earliestDeadline = await this.getEarliestApplicationDeadline(studentId);
      
      return {
        studentPosition: position,
        baselineDate: student?.timeline_baseline_date,
        resetCount: student?.timeline_reset_count || 0,
        lastCalculation: student?.last_timeline_calculation,
        earliestDeadline,
        timelinePressure: position < 0.3 ? 'low' : position < 0.7 ? 'medium' : 'high'
      };
      
    } catch (error) {
      console.error('[TimelineInterpolationService] Error getting timeline statistics:', error);
      return null;
    }
  }
}

export default TimelineInterpolationService;